import { getSoUniqueTariffRoutesByLayout } from "@/ai-generated/so-unique/router"
import { MyAccountRoute } from "@/constants/routes/myAccount.ts"
import { featureFlaggedView } from "@/plugins/router/featureFlaggedView"
import { flexMarketSubscriptionGuard } from "@/plugins/router/middleware/flexMarketSubscriptionGuard"
import { moveWithUsGuard } from "@/plugins/router/middleware/moveWithUsGuard"
import { moveWithUsRedirectGuard } from "@/plugins/router/middleware/moveWithUsRedirectGuard"
import { offersAndRewardsGuard } from "@/plugins/router/middleware/offersAndRewardsGuard"
import { renewalEligibilityGuard } from "@/plugins/router/middleware/renewalEligibilityGuard"
import { smartChargingGuard } from "@/plugins/router/middleware/smartChargingGuard"
import { soRewardingRedirectGuard } from "@/plugins/router/middleware/soRewardingRedirectGuard"
import { Layouts } from "@/types/Layout"
import { Component } from "vue"
import { RouteRecordRaw } from "vue-router"

import AccountLoadError from "@/views/myaccount/AccountLoadError.vue"
import LoginPage from "@/views/myaccount/authentication/login/Login.vue"
// Eager loaded views
import HomeView from "@/views/myaccount/home/HomeView.vue"

const renewalTitle = "Get a new fixed tariff - So Energy"

export const myAccountWebRoutes = [
  {
    // Index of the shared root parent (resolves to MyAccountRoute.home, "/").
    path: "",
    component: HomeView,
    meta: {
      requiresAuth: true,
      title: "Account Home",
      hideBackButton: true,
      showMobileUserInfoCard: true,
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: MyAccountRoute.chat,
    component: (): Promise<Component> => import("@/views/myaccount/Chat.vue"),
    meta: {
      requiresAuth: false,
      hideBackButton: true,
      layout: Layouts.CHAT,
    },
  },
  {
    path: MyAccountRoute.readings,
    component: (): Promise<Component> =>
      import("@/views/myaccount/readings/Readings.vue"),
    meta: {
      requiresAuth: true,
      title: "Meter Readings",
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: `${MyAccountRoute.readings}/submit/:id`,
    component: (): Promise<Component> =>
      import("@/views/myaccount/readings/submit/Reading.vue"),
    meta: {
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: MyAccountRoute.meterReadingHistory,
    component: (): Promise<Component> =>
      import("@/views/myaccount/readings/history/MeterReadingHistoryView.vue"),
    meta: {
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      title: "Meter Reading History",
      backButtonPath: MyAccountRoute.usage,
    },
  },
  {
    path: `${MyAccountRoute.tariff}`,
    components: {
      default: (): Promise<Component> =>
        import("@/views/myaccount/tariff/TariffView.vue"),
    },
    meta: {
      requiresAuth: true,
      title: "Tariff Details",
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: `${MyAccountRoute.usage}`,
    component: (): Promise<Component> =>
      import("@/views/myaccount/energy-usage/EnergyUsageView.vue"),
    meta: {
      title: "Energy Usage",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: `${MyAccountRoute.marketingContactPreferences}`,
    children: [
      {
        path: "",
        component: (): Promise<Component> =>
          import("@/views/myaccount/marketing-contact-preferences/MarketingContactPreferences.vue"),
      },
      {
        path: MyAccountRoute.marketingEditContactDetails,
        component: (): Promise<Component> =>
          import("@/views/myaccount/marketing-contact-preferences/EditContactDetails.vue"),
        meta: {
          backButtonPath: MyAccountRoute.marketingContactPreferences,
        },
      },
      {
        path: MyAccountRoute.marketingEditOptInDetails,
        component: (): Promise<Component> =>
          import("@/views/myaccount/marketing-contact-preferences/EditOptInDetails.vue"),
        meta: {
          backButtonPath: MyAccountRoute.marketingContactPreferences,
        },
      },
    ],

    meta: {
      title: "Marketing Contact Preferences",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      backButtonPath: MyAccountRoute.profile,
    },
  },
  {
    path: `${MyAccountRoute.profile}`,
    component: (): Promise<Component> =>
      import("@/views/myaccount/profile/Profile.vue"),
    meta: {
      title: "Profile",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      hideBackButton: true,
    },
  },
  {
    path: `${MyAccountRoute.help}`,
    component: (): Promise<Component> =>
      import("@/views/myaccount/help/Help.vue"),
    meta: {
      title: "Help & Advice",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: MyAccountRoute.referAFriend,
    component: (): Promise<Component> =>
      import("@/views/myaccount/refer/ReferAFriend.vue"),
    meta: {
      title: "Refer a Friend",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: MyAccountRoute.movingHome,
    beforeEnter: moveWithUsGuard,
    component: (): Promise<Component> =>
      import("@/views/myaccount/moving-home/MovingHome.vue"),
    meta: {
      title: "Moving Home",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      backButtonPath: MyAccountRoute.home,
    },
  },
  {
    path: MyAccountRoute.moveWithUsStatus,
    beforeEnter: moveWithUsRedirectGuard,
    component: (): Promise<Component> =>
      import("@/views/myaccount/moving-home/MoveWithUsStatus.vue"),
    meta: {
      title: "Moving Home",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      backButtonPath: MyAccountRoute.home,
    },
  },
  {
    path: MyAccountRoute.moveWithUs,
    beforeEnter: [moveWithUsRedirectGuard, moveWithUsGuard],
    redirect: MyAccountRoute.moveWithUsMoveDetails,
    component: (): Promise<Component> =>
      import("@/views/myaccount/moving-home/MoveWithUsContainer.vue"),
    meta: { layout: Layouts.EMPTY, requiresAuth: true },
    children: [
      {
        path: MyAccountRoute.moveWithUsMoveDetails,
        component: (): Promise<Component> =>
          import("@/views/myaccount/moving-home/steps/MoveDetails.vue"),
      },
      {
        path: MyAccountRoute.moveWithUsNewAddress,
        component: (): Promise<Component> =>
          import("@/views/myaccount/moving-home/steps/NewAddress.vue"),
      },
      {
        path: MyAccountRoute.moveWithUsTariff,
        component: (): Promise<Component> =>
          import("@/views/myaccount/moving-home/steps/Tariff.vue"),
      },
      {
        path: MyAccountRoute.moveWithUsPayment,
        component: (): Promise<Component> =>
          import("@/views/myaccount/moving-home/steps/Payment.vue"),
      },
      {
        path: MyAccountRoute.moveWithUsReview,
        component: (): Promise<Component> =>
          import("@/views/myaccount/moving-home/steps/Review.vue"),
      },
    ],
  },
  {
    path: MyAccountRoute.moveWithUsConfirmation,
    component: (): Promise<Component> =>
      import("@/views/myaccount/moving-home/steps/Confirmation.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: MyAccountRoute.renewal,
    beforeEnter: renewalEligibilityGuard,
    redirect: MyAccountRoute.renewalChoosePlan,
    component: (): Promise<Component> =>
      import("@/views/myaccount/renewal/RenewalContainer.vue"),
    meta: {
      layout: Layouts.EMPTY,
      requiresAuth: true,
      metaTitle: renewalTitle,
    },
    children: [
      {
        path: MyAccountRoute.renewalChoosePlan,
        component: (): Promise<Component> =>
          import("@/views/myaccount/renewal/steps/RenewalChoosePlanStep.vue"),
      },
      {
        path: MyAccountRoute.renewalDirectDebit,
        component: (): Promise<Component> =>
          import("@/views/myaccount/renewal/steps/RenewalDirectDebitStep.vue"),
      },
      {
        path: MyAccountRoute.renewalReview,
        component: (): Promise<Component> =>
          import("@/views/myaccount/renewal/steps/RenewalReviewStep.vue"),
      },
    ],
  },
  {
    path: MyAccountRoute.renewalPending,
    component: (): Promise<Component> =>
      import("@/views/myaccount/renewal/RenewalPendingView.vue"),
    meta: {
      requiresAuth: true,
      layout: Layouts.SINGLE_PAGE,
      metaTitle: renewalTitle,
    },
  },
  {
    path: MyAccountRoute.renewalComplete,
    component: (): Promise<Component> =>
      import("@/views/myaccount/renewal/RenewalConfirmationView.vue"),
    meta: {
      requiresAuth: true,
      layout: Layouts.SINGLE_PAGE,
      metaTitle: renewalTitle,
    },
  },
  {
    path: MyAccountRoute.renewalUnavailable,
    component: (): Promise<Component> =>
      import("@/views/myaccount/renewal/RenewalUnavailableView.vue"),
    meta: {
      requiresAuth: true,
      layout: Layouts.SINGLE_PAGE,
      metaTitle: renewalTitle,
    },
  },
  {
    path: MyAccountRoute.login,
    component: LoginPage,
    meta: {
      requiresAnon: true,
      hideBackButton: true,
    },
  },
  {
    // No requiresAnon/requiresAuth: reached by an authenticated customer whose
    // customer fetch failed, but must stay renderable either way
    path: MyAccountRoute.accountLoadError,
    component: AccountLoadError,
    meta: {
      hideBackButton: true,
    },
  },
  {
    path: MyAccountRoute.moveIn,
    component: (): Promise<Component> =>
      import("@/views/myaccount/cot-move-in/CotMoveInContainer.vue"),
    meta: {
      layout: Layouts.EMPTY,
      requiresAuth: false,
    },
    children: [
      {
        path: MyAccountRoute.cotMoveInFindAddress,
        component: (): Promise<Component> =>
          import("@/views/myaccount/cot-move-in/CotMoveInFindAddress.vue"),
      },
      {
        path: MyAccountRoute.cotMoveInFdor,
        component: (): Promise<Component> =>
          import("@/views/myaccount/cot-move-in/CotMoveInFdor.vue"),
      },
      {
        path: MyAccountRoute.cotMoveInMeterReading,
        component: (): Promise<Component> =>
          import("@/views/myaccount/cot-move-in/CotMeterReading.vue"),
      },
      {
        path: MyAccountRoute.cotMoveInPersonalDetails,
        component: (): Promise<Component> =>
          import("@/views/myaccount/cot-move-in/CotMoveInPersonalDetails.vue"),
      },
      {
        path: MyAccountRoute.cotMoveInPayments,
        component: (): Promise<Component> =>
          import("@/views/myaccount/cot-move-in/CotMoveInPayments.vue"),
      },
      {
        path: MyAccountRoute.cotMoveInReview,
        component: (): Promise<Component> =>
          import("@/views/myaccount/cot-move-in/CotMoveInReview.vue"),
      },
      {
        path: MyAccountRoute.cotMoveInMonthlyPayment,
        component: (): Promise<Component> =>
          import("@/views/myaccount/cot-move-in/CotMoveInMonthlyPayment.vue"),
      },
    ],
  },
  {
    path: MyAccountRoute.cotMoveInComplete,
    component: (): Promise<Component> =>
      import("@/views/myaccount/cot-move-in/CotMoveInConfirmation.vue"),
  },
  {
    path: MyAccountRoute.spayg,
    component: (): Promise<Component> =>
      import("@/views/myaccount/smart-pay-as-you-go/SpaygContainer.vue"),
    meta: {
      layout: Layouts.EMPTY,
      requiresAuth: true,
    },
    children: [
      {
        path: MyAccountRoute.spaygWhy,
        component: (): Promise<Component> =>
          import("@/views/myaccount/smart-pay-as-you-go/SpaygWhy.vue"),
      },
      {
        path: MyAccountRoute.spaygSuitability,
        component: (): Promise<Component> =>
          import("@/views/myaccount/smart-pay-as-you-go/SpaygSuitability.vue"),
      },
      {
        path: MyAccountRoute.spaygFuel,
        component: (): Promise<Component> =>
          import("@/views/myaccount/smart-pay-as-you-go/SpaygFuel.vue"),
      },
      {
        path: MyAccountRoute.spaygRepayment,
        component: (): Promise<Component> =>
          import("@/views/myaccount/smart-pay-as-you-go/SpaygRepayment.vue"),
      },
      {
        path: MyAccountRoute.spaygSwitchDate,
        component: (): Promise<Component> =>
          import("@/views/myaccount/smart-pay-as-you-go/SpaygSwitchDate.vue"),
      },
      {
        path: MyAccountRoute.spaygTariff,
        component: (): Promise<Component> =>
          import("@/views/myaccount/smart-pay-as-you-go/SpaygTariff.vue"),
      },
      {
        path: MyAccountRoute.spaygReview,
        component: (): Promise<Component> =>
          import("@/views/myaccount/smart-pay-as-you-go/SpaygReview.vue"),
      },
    ],
  },
  {
    meta: {
      requiresAuth: true,
    },
    path: MyAccountRoute.spaygComplete,
    component: (): Promise<Component> =>
      import("@/views/myaccount/smart-pay-as-you-go/SpaygConfirmation.vue"),
  },
  {
    path: MyAccountRoute.meterReadFrequency,
    component: (): Promise<Component> =>
      import("@/views/myaccount/meter-read-frequency/MeterReadFrequencyView.vue"),
    meta: {
      requiresAuth: true,
      layout: Layouts.SINGLE_PAGE,
      title: "Meter Frequency",
      backButtonPath: MyAccountRoute.profile,
    },
  },
  {
    path: MyAccountRoute.passwordReset,
    component: (): Promise<Component> =>
      import("@/views/myaccount/authentication/password-reset/PasswordReset.vue"),
    meta: {
      requiresAnon: true,
    },
  },
  {
    path: MyAccountRoute.passwordChange,
    component: (): Promise<Component> =>
      import("@/views/myaccount/authentication/password-change/PasswordChange.vue"),
    meta: {
      requiresAnon: true,
    },
  },
  {
    path: MyAccountRoute.activateAccount,
    component: (): Promise<Component> =>
      import("@/views/myaccount/authentication/activate-account/ActivateAccount.vue"),
    meta: {
      requiresAnon: true,
    },
  },
  {
    path: MyAccountRoute.setPassword,
    component: (): Promise<Component> =>
      import("@/views/myaccount/authentication/set-password/SetPassword.vue"),
    meta: {
      requiresAnon: true,
    },
  },
  {
    path: MyAccountRoute.flexMarketSubscriptionDetails,
    beforeEnter: flexMarketSubscriptionGuard,
    component: (): Promise<Component> =>
      import("@/views/myaccount/flex-market-subscription/FlexMarketSubscriptionDetailsView.vue"),
    meta: {
      title: "So Charged",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: MyAccountRoute.flexMarketSubscriptionSignUp,
    beforeEnter: flexMarketSubscriptionGuard,
    component: (): Promise<Component> =>
      import("@/views/myaccount/flex-market-subscription/FlexMarketSubscriptionSignUpView.vue"),
    meta: {
      title: "So Charged - Sign Up",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      backButtonPath: MyAccountRoute.flexMarketSubscriptionDetails,
    },
  },
  {
    path: MyAccountRoute.flexMarketSubscriptionApp,
    beforeEnter: flexMarketSubscriptionGuard,
    component: (): Promise<Component> =>
      import("@/views/myaccount/flex-market-subscription/FlexMarketSubscriptionAppView.vue"),
    meta: {
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: MyAccountRoute.smartChargingRenewalConfirmation,
    component: (): Promise<Component> =>
      import("@/views/myaccount/smart-charging/SmartChargingRenewalConfirmationView.vue"),
    meta: {
      requiresAuth: true,
      title: "So EV Connected",
      layout: Layouts.SINGLE_PAGE,
      hideBackButton: true,
    },
  },
  {
    path: MyAccountRoute.smartChargingApp,
    beforeEnter: smartChargingGuard,
    component: (): Promise<Component> =>
      import("@/views/myaccount/smart-charging/SmartChargingAppView.vue"),
    meta: {
      requiresAuth: true,
      title: "So EV Connected",
      layout: Layouts.SIDEBAR,
    },
  },
  {
    path: MyAccountRoute.evChargerQuote,
    component: (): Promise<Component> =>
      import("@/views/myaccount/ev-charger-quote/EvChargerQuoteContainer.vue"),
    meta: {
      layout: Layouts.EMPTY,
      requiresAuth: false,
    },
    children: [
      {
        path: MyAccountRoute.evChargerQuoteAddress,
        component: (): Promise<Component> =>
          import("@/views/myaccount/ev-charger-quote/EvChargerQuoteAddress.vue"),
      },
      {
        path: MyAccountRoute.evChargerQuoteParking,
        component: (): Promise<Component> =>
          import("@/views/myaccount/ev-charger-quote/EvChargerQuoteParking.vue"),
      },
      {
        path: MyAccountRoute.evChargerQuotePersonalDetails,
        component: (): Promise<Component> =>
          import("@/views/myaccount/ev-charger-quote/EvChargerQuotePersonalDetails.vue"),
      },
    ],
  },
  {
    path: MyAccountRoute.evChargerQuoteNoParking,
    component: (): Promise<Component> =>
      import("@/views/myaccount/ev-charger-quote/EvChargerQuoteNoParking.vue"),
  },
  {
    path: MyAccountRoute.evChargerQuoteConfirmation,
    component: (): Promise<Component> =>
      import("@/views/myaccount/ev-charger-quote/EvChargerQuoteConfirmation.vue"),
  },
  {
    path: MyAccountRoute.psr,
    component: (): Promise<Component> =>
      import("@/views/myaccount/psr/PSRView.vue"),
    meta: {
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      title: "Priority Services Register",
      hideBackButton: false,
      userInfoCardDesktopOnly: true,
      backButtonPath: MyAccountRoute.profile,
    },
  },
  {
    path: MyAccountRoute.psrForm,
    component: (): Promise<Component> =>
      import("@/views/myaccount/psr/PsrFormContainer.vue"),
    meta: {
      requiresAuth: true,
      title: "Priority Services Register",
      hideBackButton: true,
      layout: Layouts.EMPTY,
      closeUrl: MyAccountRoute.psr,
    },
  },
  {
    path: MyAccountRoute.psrConfirmation,
    component: () => import("@/views/myaccount/psr/PSRConfirmation.vue"),
    meta: {
      requiresAuth: true,
      hideBackButton: true,
    },
  },
  {
    path: MyAccountRoute.prizeDraw,
    meta: {
      layout: Layouts.SINGLE_PAGE,
      requiresAuth: true,
      hideBackButton: true,
    },
    children: [
      {
        path: "",
        component: (): Promise<Component> =>
          import("@/views/myaccount/loyalty/PrizeDrawView.vue"),
        beforeEnter: soRewardingRedirectGuard,
        meta: {
          title: "Enter our Prize Draw",
        },
      },
      {
        path: MyAccountRoute.prizeDrawAlreadySubmitted,
        component: (): Promise<Component> =>
          import("@/views/myaccount/loyalty/PrizeDrawAlreadySubmitted.vue"),
        meta: {
          title: "You're all set!",
        },
      },
    ],
  },
  {
    path: MyAccountRoute.accountRecovery,
    meta: {
      title: "Account Recovery",
      layout: Layouts.SINGLE_PAGE,
      requiresAnon: true,
      hideBackButton: true,
      metaTitle: "Account Recovery - So Energy",
    },
    children: [
      {
        path: "",
        component: () =>
          import("@/views/myaccount/account-recovery/AccountRecoveryPersonalInfo.vue"),
      },
      {
        path: MyAccountRoute.accountRecoveryAccountDetails,
        component: () =>
          import("@/views/myaccount/account-recovery/AccountRecoveryAccountDetails.vue"),
      },
      {
        path: MyAccountRoute.accountRecoveryNotFound,
        component: () =>
          import("@/views/myaccount/account-recovery/AccountRecoveryNotFound.vue"),
      },
    ],
  },
  {
    path: MyAccountRoute.accountRecoveryConfirmation,
    component: () =>
      import("@/views/myaccount/account-recovery/AccountRecoveryConfirmation.vue"),
    meta: {
      requiresAnon: true,
      layout: Layouts.SINGLE_PAGE,
    },
  },
  {
    path: MyAccountRoute.billsAndTransactions,
    meta: {
      title: "Bills and Transactions",
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
    },
    children: [
      {
        path: "",
        component: (): Promise<Component> =>
          import("@/views/myaccount/bills-and-transactions/BillsAndTransactions.vue"),
      },
      {
        path: MyAccountRoute.billDetail,
        component: (): Promise<Component> =>
          import("@/views/myaccount/bills-and-transactions/BillDetails.vue"),
        meta: {
          backButtonPath: MyAccountRoute.billsAndTransactions,
        },
      },
    ],
  },
  {
    path: MyAccountRoute.cardPayment,
    component: () =>
      import("@/views/myaccount/payments/MakePaymentFlowView.vue"),
    meta: {
      requiresAuth: true,
      title: "Make a payment",
      layout: Layouts.SINGLE_PAGE,
      hideBackButton: false,
    },
  },
  {
    path: `${MyAccountRoute.viewTransaction}/:identifier`,
    component: (): Promise<Component> =>
      import("@/views/myaccount/payments/ViewTransactionView.vue"),
    meta: {
      requiresAuth: true,
      title: "View Transaction",
      layout: Layouts.SINGLE_PAGE,
      hideBackButton: true,
    },
  },
  {
    path: MyAccountRoute.paymentSuccessful,
    meta: { requiresAuth: false },
    redirect: (to) => ({
      path: MyAccountRoute.cardPayment,
      query: { status: "success", ...to.query },
    }),
  },
  {
    path: MyAccountRoute.paymentFailed,
    meta: { requiresAuth: false },
    redirect: (to) => ({
      path: MyAccountRoute.cardPayment,
      query: { status: "failed", ...to.query },
    }),
  },
  {
    path: MyAccountRoute.rewards,
    beforeEnter: offersAndRewardsGuard,
    component: featureFlaggedView("soRewardingV2", {
      off: () =>
        import("@/views/myaccount/maintenance/SoRewardingMaintenance.vue"),
      on: () => import("@/views/myaccount/loyalty/SoRewardingView.vue"),
    }),
    meta: {
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      title: "Offers & Rewards",
      hideBackButton: false,
      userInfoCardDesktopOnly: true,
      backButtonPath: MyAccountRoute.home,
    },
  },
  // Legacy /rewards path — kept for marketing links and customer bookmarks.
  {
    path: "/rewards",
    component: featureFlaggedView("soRewardingV2", {
      off: () =>
        import("@/views/myaccount/maintenance/SoRewardingMaintenance.vue"),
      on: () => import("@/views/myaccount/loyalty/RewardsView.vue"),
    }),
    beforeEnter: soRewardingRedirectGuard,
    meta: {
      requiresAuth: true,
      layout: Layouts.SIDEBAR,
      title: "Offers & Rewards",
      hideBackButton: false,
      userInfoCardDesktopOnly: true,
      backButtonPath: MyAccountRoute.home,
    },
  },
  { path: "/rewards/join", redirect: MyAccountRoute.rewardsJoin },
  { path: "/rewards/joined", redirect: MyAccountRoute.rewardsJoined },
  {
    path: MyAccountRoute.rewardsJoin,
    beforeEnter: offersAndRewardsGuard,
    component: featureFlaggedView("soRewardingV2", {
      off: () =>
        import("@/views/myaccount/maintenance/SoRewardingMaintenance.vue"),
      on: () => import("@/views/myaccount/loyalty/RewardsJoinView.vue"),
    }),
    meta: {
      requiresAuth: true,
      layout: Layouts.EMPTY,
      title: "Join So Rewarding",
    },
  },
  {
    path: MyAccountRoute.rewardsJoined,
    beforeEnter: offersAndRewardsGuard,
    component: featureFlaggedView("soRewardingV2", {
      off: () =>
        import("@/views/myaccount/maintenance/SoRewardingMaintenance.vue"),
      on: () => import("@/views/myaccount/loyalty/RewardsJoinedView.vue"),
    }),
    meta: {
      requiresAuth: true,
      layout: Layouts.EMPTY,
      title: "Welcome to So Rewarding",
    },
  },
  ...getSoUniqueTariffRoutesByLayout(Layouts.EMPTY),
] satisfies readonly RouteRecordRaw[]
